import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ButtonBack from '../components/ButtonBack'
import Card from '../components/carousel/Card'
import { getPromoById } from '../services/promosService'
import { useAuthContext } from '../hooks/useAuthContext'
import copys, { termsAndPrivacy } from '../utils/copys'
import { localStorageKeyNames, promoStatus, routePaths, ToastTypesList } from '../utils/constants'
import CardQR from '../components/CardQR'
import { usePromoContext } from '../hooks/usePromoContext'
import ExpiredPromoCard from '../components/ExpiredPromoCard'
import DetailInfoCard from '../components/DetailInfoCard'
import CardPromoStatus from '../components/CardPromoStatus'
import ScreenLoading from './ScreenLoading'
import LockImg from '../assets/ilustraciones/lock.svg'
import TimeImg from '../assets/ilustraciones/time.svg'
import BrokenCouponImg from '../assets/ilustraciones/broken-coupon.svg'
import CouponCounter from '../components/CouponCounter'
import CountdownTimer from '../components/CountdownTimer'
import * as gtmService from '../services/gtmService';
import { checkIfIsExpired } from '../utils/dateUtils'
import { useToast } from '../hooks/useToast'
import * as Sentry from '@sentry/react'
import Modal from '../components/modals/Modal'
import ModalTerms, { ModelContentType } from '../components/modals/ModalTerms'
import { acceptNewTerms, sendErrorReceivedConfirmation } from '../services/authService/authService'
import { Legals, PromotionDetail } from '../models/promotions'
import { useRequest } from '../hooks/useRequest'

const ScreenPromoDetail = () => {

    const { userData, setUserData } = useAuthContext();
    const { setPromoData } = usePromoContext();
    const navigate = useNavigate()
    const { promoId } = useParams()
    const [promo, setPromo] = useState<PromotionDetail>();
    const [showLoading, setShowLoading] = useState(true);
    const [isTimerDone, setIsTimerDone] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalContentType, setModalContentType] = useState<ModelContentType>();
    const [publicContentId, setPublicContentId] = useState<string[]>([]);
    const { showToast } = useToast();
    const { requestWrapper } = useRequest();


    // Use a ref to access the current promo value in
    // an async callback.
    const promoRef = useRef(promo);
    promoRef.current = promo;

    const initialTimeoutId = useRef<NodeJS.Timeout>();


    const checkIfThereAreNewTerms = (legals: Legals) => {
        if (legals) {
            const { has_new_terms, has_new_policies, terms_conditions, policies_privacy } = legals;
            if (has_new_terms || has_new_policies) {
                setShowModal(true);
                setModalContentType(has_new_terms && has_new_policies ? 'both' : has_new_terms ? 'terms' : 'privacy');
                const contentIds = [];
                if (has_new_terms) contentIds.push(terms_conditions.public_content_id);
                if (has_new_policies) contentIds.push(policies_privacy.public_content_id);
                setPublicContentId(contentIds);
            }
        }
    }

    const confirmErrorReceived = async (code: string) => {
        await sendErrorReceivedConfirmation({
            code,
            promotion_id: promoId!
        })
    }

    const checkIsHasExchanged = async () => {
        const promotion = promoRef.current;
        if (promotion?.status === promoStatus.ACTIVE && document.visibilityState === 'visible') {
            const { successful, data } = await requestWrapper(() => getPromoById(promoId!))
            if (successful) {
                if(data.hasReachedLimitExchangesToday) {
                    showToast(ToastTypesList.ERROR, copys.toastDailyLimitReached);
                    goToHome();
                    return;
                }
                const isPromoExpired = checkIfIsExpired(data.end_date);
                if (isPromoExpired) {
                    setPromo({ status: promoStatus.EXPIRED })
                } else {
                    if (!data.is_available) {
                        showToast(ToastTypesList.INFO, copys.toastCantRedeemInCurrentStore);
                        confirmErrorReceived(data.code).finally(() => {
                            goToHome();
                        });
                    }
                    if (data.status !== promotion.status) {
                        goToHome();
                    }
                }
                checkIfThereAreNewTerms(data.legals);
            }
        }
    }

    const activateCouponAndRefresh = async (promotion: PromotionDetail) => {
        const { successful, code } = await requestWrapper(() => getPromoById(promoId!))

        if (successful) {
            setPromo({ ...promotion, status: promoStatus.ACTIVE, code })
        } else {
            setPromo({ status: promoStatus.EXPIRED })
        }
    }

    const goToLogin = () => {
        setPromoData({ promoId: promo!.id! })
        navigate('/' + routePaths.LOGIN)
    }

    const goToHome = () => navigate('/' + routePaths.HOME);

    const generateDetailCard = () => {
        let detailCard;
        if (userData.isLogged) {
            switch (promo?.status) {
                case promoStatus.ACTIVE:
                    detailCard = <CardQR code={promo?.code!} />
                    break;
                case promoStatus.ONHOLD:
                    const title = isTimerDone ? 'Disponible, recarga la página' : <>{copys.buttonAvailable24h} <CountdownTimer targetDate={promo?.last_exchange_time!} onComplete={() => setIsTimerDone(true)} /></>
                    detailCard = <CardPromoStatus labels={{ title, text: copys.detailCardOnHoldText }} buttonLabel={copys.buttonSeeMoreCoupons} onClick={goToHome}><img src={TimeImg} alt='coupon status' className='w-20 aspect-square' /></CardPromoStatus>
                    break;
                case promoStatus.DISABLED:
                    const labels = {
                        title: copys.detailCardDisabledTitle,
                        text: copys.detailCardDisabledText
                    };
                    detailCard = <CardPromoStatus labels={labels} onClick={goToHome} buttonLabel={copys.buttonSeeMoreCoupons} ><img src={BrokenCouponImg} alt='coupon status' className='aspect-[1.725] w-[136px]' /></CardPromoStatus>
                    break;
                default:
                    detailCard = '';
                    break;
            }
        } else {
            detailCard = <CardPromoStatus labels={{ title: copys.detailCardNotLogged }} buttonLabel={copys.headerLoginButton} onClick={goToLogin}><img src={LockImg} alt='coupon status' className='w-[60.2px] aspect-[0.7625]' /></CardPromoStatus>;
        }
        return detailCard;
    }

    const logout = () => {
        setUserData({ isLogged: false, otpRetries: [] });
        localStorage.removeItem(localStorageKeyNames.userRegisterDate);
        navigate('/');
    }

    const acceptOrRejectTerms = async (action: 'accept' | 'reject') => {
        const isAccepted = action === 'accept' ? true : false;
        const responses = await Promise.all(
            publicContentId.map(id => acceptNewTerms({
                public_content_id: id,
                isAccepted,
            })
            )
        );
        if (responses.some(r => !r.successful)) {
            showToast(ToastTypesList.ERROR, copys.toastAcceptNewTermsFailed);
        }
        if (action === 'accept') {
            setShowModal(false);
        } else {
            logout();
        }
    }



    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        window.scrollTo(0, 0);

        const startPolling = async () => {
            if (document.visibilityState === 'visible') {
                await checkIsHasExchanged();
            }
            timeoutId = setTimeout(startPolling, 7000); // Start next polling after 10 seconds
        };

        const loadPromo = async (promoId: string) => {
            const { successful, data, errors, message } = await requestWrapper(() => getPromoById(promoId));
            if (successful) {
                const isPromoExpired = checkIfIsExpired(data.end_date);
                if (isPromoExpired) {
                    setPromo({ status: promoStatus.EXPIRED })
                } else {
                    checkIfThereAreNewTerms(data.legals);
                    setPromo(data);
                    if (userData.isLogged && data.status === promoStatus.AVAILABLE) {
                        await activateCouponAndRefresh(data)
                    }
                }
                gtmService.pushShowCoupon(promoId, data.description, data.code || '');
                initialTimeoutId.current = setTimeout(() => startPolling(), 7000);
            } else {
                console.log('Error al cargar detalle de cupon', errors)
                if (data.code === 404) {
                    navigate('/home');
                    return;
                }
                Sentry.captureException(errors, {
                    tags: {
                        flow: 'load_promotion_details',
                        promotion_id: promoId,
                        user_id: userData?.user?.id,
                    },
                });
                showToast(ToastTypesList.ERROR, message);
            }
            gtmService.pushPageView('PDP')
            setShowLoading(false)
            setPromoData({ promoId: '0' })
        };

        loadPromo(promoId!);

        return () => {
            clearTimeout(timeoutId); // Cleanup on unmount
            clearTimeout(initialTimeoutId.current); // Cleanup on unmount
        }

    }, [])

    return (
        <div className='flex flex-col justify-start'>
            <ButtonBack customPage={'/home'} customLabel={copys.backButtonGoHome} />
            {showLoading && <ScreenLoading />}
            <Modal isOpen={showModal} isCentered>
                <ModalTerms modalContentType={modalContentType} onClickAccept={() => acceptOrRejectTerms('accept')} onClickReject={() => acceptOrRejectTerms('reject')} />
            </Modal>
            <div className="flex justify-center">
                <div className="flex flex-col items-center p-6 gap-4 min-[370px]:w-[370px]">
                    {
                        promo && promo?.status !== promoStatus.EXPIRED && (
                            <>
                                <Card promo={promo} status={promo.status} isImageOnly />
                                {
                                    generateDetailCard()
                                }
                                {userData.isLogged && <CouponCounter count={promo.exchanges_count} exchangesByPromoAllowed={promo.exchanges_allowed} />}
                                <DetailInfoCard promo={promo} html={promo.public_content?.content || ''} />
                            </>
                        )
                    }
                    {
                        promo?.status === promoStatus.EXPIRED && (
                            <ExpiredPromoCard />
                        )
                    }

                </div>
            </div>


        </div>
    )
}

export default ScreenPromoDetail
